import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { type AuthenticatedUser } from '@archimed/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  type AccountInfo,
  type AuthenticationResult,
  type EndSessionRequest,
  type EventError,
  type EventMessage,
  EventType,
} from '@azure/msal-browser';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly msalService = inject(MsalService);
  private readonly router = inject(Router);

  public connectedUser: BehaviorSubject<AuthenticatedUser | null> =
    new BehaviorSubject<AuthenticatedUser | null>(null);

  constructor() {
    // Init Service
    this._activeFirstAccountFromSessionStorage();
    this._loadActiveAccount();

    this.msalBroadcastService.msalSubject$.subscribe({
      next: (event: EventMessage) => {
        switch (event.eventType) {
          case EventType.LOGIN_SUCCESS:
          case EventType.ACQUIRE_TOKEN_SUCCESS: {
            const payload = event.payload as AuthenticationResult;
            this._onLogin(payload.account);
            break;
          }

          case EventType.LOGOUT_SUCCESS: {
            this._onLogout();
            break;
          }

          case EventType.ACQUIRE_TOKEN_FAILURE: {
            this._onLoginFailure(event.error);
            break;
          }

          default: {
            break;
          }
        }
      },
    });
  }

  public logout(logoutRequest?: EndSessionRequest) {
    this.msalService.logoutPopup(logoutRequest);
  }

  public async acquireToken(): Promise<string> {
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];
    const { accessToken } = await this.msalService.instance.acquireTokenSilent({
      scopes: environment.authentication.scopes,
      account,
    });
    return accessToken;
  }

  private _onLogin(account: AccountInfo | null) {
    if (!account) {
      return;
    }

    this.msalService.instance.setActiveAccount(account);
    this._loadActiveAccount();
  }

  private _onLogout() {
    this.connectedUser.next(null);
  }

  private _onLoginFailure(error: EventError) {
    this.connectedUser.next(null);
    console.error(error);
    this.router.navigate(['/refinery/explore']);
  }

  private _accountInfoToConnectedUser(
    accountInfo: AccountInfo | null,
  ): null | AuthenticatedUser {
    if (!accountInfo) {
      return null;
    }

    const idTokenClaims = accountInfo.idTokenClaims as
      | Record<string, unknown>
      | undefined;

    return {
      displayName: accountInfo.name ?? accountInfo.username,
      oid: accountInfo.localAccountId,
      roles: (idTokenClaims?.['roles'] as string[]) ?? [],
      sub: accountInfo.homeAccountId,
      tenantId: accountInfo.tenantId,
      username: accountInfo.username,
    } as AuthenticatedUser;
  }

  private _loadActiveAccount() {
    if (!this.msalService.instance.getActiveAccount()) {
      return;
    }
    const activeAccount = this._accountInfoToConnectedUser(
      this.msalService.instance.getActiveAccount(),
    );
    this.connectedUser.next(activeAccount);
  }

  private _activeFirstAccountFromSessionStorage() {
    const account = this.msalService.instance.getAllAccounts();
    if (account && account.length > 0) {
      this.msalService.instance.setActiveAccount(account[0]);
    }
  }
}
